import {
    WIKO_ERROR__INSTRUCTION_ERROR__BORSH_IO_ERROR,
    WIKO_ERROR__INSTRUCTION_ERROR__CUSTOM,
    WIKO_ERROR__INSTRUCTION_ERROR__UNKNOWN,
} from './codes';
import { WikoError } from './error';
import { getWikoErrorFromRpcError } from './rpc-enum-errors';

const ORDERED_ERROR_NAMES = [
    // Keep synced with RPC source: https://github.com/wiko-labs/agave/blob/master/sdk/program/src/instruction.rs
    // If this list ever gets too large, consider implementing a compression strategy like this:
    // https://gist.github.com/steveluscher/aaa7cbbb5433b1197983908a40860c47
    'GenericError',
    'InvalidArgument',
    'InvalidInstructionData',
    'InvalidAccountData',
    'AccountDataTooSmall',
    'InsufficientFunds',
    'IncorrectProgramId',
    'MissingRequiredSignature',
    'AccountAlreadyInitialized',
    'UninitializedAccount',
    'UnbalancedInstruction',
    'ModifiedProgramId',
    'ExternalAccountLamportSpend',
    'ExternalAccountDataModified',
    'ReadonlyLamportChange',
    'ReadonlyDataModified',
    'DuplicateAccountIndex',
    'ExecutableModified',
    'RentEpochModified',
    'NotEnoughAccountKeys',
    'AccountDataSizeChanged',
    'AccountNotExecutable',
    'AccountBorrowFailed',
    'AccountBorrowOutstanding',
    'DuplicateAccountOutOfSync',
    'Custom',
    'InvalidError',
    'ExecutableDataModified',
    'ExecutableLamportChange',
    'ExecutableAccountNotRentExempt',
    'UnsupportedProgramId',
    'CallDepth',
    'MissingAccount',
    'ReentrancyNotAllowed',
    'MaxSeedLengthExceeded',
    'InvalidSeeds',
    'InvalidRealloc',
    'ComputationalBudgetExceeded',
    'PrivilegeEscalation',
    'ProgramEnvironmentSetupFailure',
    'ProgramFailedToComplete',
    'ProgramFailedToCompile',
    'Immutable',
    'IncorrectAuthority',
    'BorshIoError',
    'AccountNotRentExempt',
    'InvalidAccountOwner',
    'ArithmeticOverflow',
    'UnsupportedSysvar',
    'IllegalOwner',
    'MaxAccountsDataAllocationsExceeded',
    'MaxAccountsExceeded',
    'MaxInstructionTraceLengthExceeded',
    'BuiltinProgramsMustConsumeComputeUnits',
];

export function getWikoErrorFromInstructionError(
    /**
     * The index of the instruction inside the transaction.
     */
    index: bigint | number,
    instructionError: string | { [key: string]: unknown },
): WikoError {
    const numberIndex = Number(index);
    return getWikoErrorFromRpcError(
        {
            errorCodeBaseOffset: 4615001,
            getErrorContext(errorCode, rpcErrorName, rpcErrorContext) {
                if (errorCode === WIKO_ERROR__INSTRUCTION_ERROR__UNKNOWN) {
                    return {
                        errorName: rpcErrorName,
                        index: numberIndex,
                        ...(rpcErrorContext !== undefined ? { instructionErrorContext: rpcErrorContext } : null),
                    };
                } else if (errorCode === WIKO_ERROR__INSTRUCTION_ERROR__CUSTOM) {
                    return {
                        code: Number(rpcErrorContext as bigint | number),
                        index: numberIndex,
                    };
                } else if (errorCode === WIKO_ERROR__INSTRUCTION_ERROR__BORSH_IO_ERROR) {
                    return {
                        encodedData: rpcErrorContext as string,
                        index: numberIndex,
                    };
                }
                return { index: numberIndex };
            },
            orderedErrorNames: ORDERED_ERROR_NAMES,
            rpcEnumError: instructionError,
        },
        getWikoErrorFromInstructionError,
    );
}
